/**
 * Kanze global script. Loaded once, deferred, on every page.
 *
 * window.Kanze.money(amount) is the only way JavaScript may format a price.
 * It never assumes how many decimals a currency has. Instead it reads two sample
 * prices that Liquid printed with the store's real format (see snippets/scripts.liquid):
 *
 *   sample       the money snippet applied to 100
 *   sampleLarge  the money snippet applied to 123456789
 *
 * From the first it learns the divisor, the number of decimals and the decimal
 * separator. From the second it learns the thousands separator. Everything around
 * the number (currency symbol, "KD" suffix) is kept as written.
 *
 * On Arabic pages the samples can be in Arabic-Indic digits ("١٫٠٠٠ د.ك"). They are read
 * the same way, and prices are then written back in those digits with the Arabic
 * separators. window.Kanze.digits(text) does the same for plain numbers such as counts.
 */
(() => {
  const SAMPLE_AMOUNT = 100;
  const SAMPLE_LARGE_AMOUNT = 123456789;
  const NUMBER = /\d(?:[\d.,'’   ]*\d)?/;
  const ARABIC_DIGITS = '٠١٢٣٤٥٦٧٨٩';
  const ARABIC_DECIMAL = '٫';
  const ARABIC_THOUSANDS = '٬';

  /** Rewrites Arabic-Indic digits and separators as the Western ones, so a sample price can be parsed. */
  const toWestern = (text) =>
    String(text || '')
      .replace(/[٠-٩]/g, (digit) => String(ARABIC_DIGITS.indexOf(digit)))
      .replaceAll(ARABIC_DECIMAL, '.')
      .replaceAll(ARABIC_THOUSANDS, ',');

  /** Rewrites Western digits as Arabic-Indic ones. */
  const toArabic = (text) => String(text).replace(/\d/g, (digit) => ARABIC_DIGITS[digit]);

  /** Splits a formatted price into the text before the number, the number, and the text after. */
  function splitSample(text) {
    const match = String(text || '').match(NUMBER);
    if (!match) return null;
    return {
      prefix: text.slice(0, match.index),
      number: match[0],
      suffix: text.slice(match.index + match[0].length),
    };
  }

  /**
   * Works out the money format from the two samples.
   * @param {{sample: string, sampleLarge: string}} config
   */
  function readFormat(config) {
    const fallback = { prefix: '', suffix: '', divisor: 100, decimals: 2, decimalSeparator: '.', thousandsSeparator: ',', arabic: false };
    const arabic = /[٠-٩]/.test((config && config.sample) || '');
    const small = splitSample(toWestern(config && config.sample));
    if (!small) return fallback;

    // A price of 100 is too small to contain a thousands separator,
    // so any separator in it is the decimal separator.
    const separatorIndex = small.number.search(/\D(?=\d*$)/);
    const decimalSeparator = separatorIndex === -1 ? '' : small.number[separatorIndex];
    const decimals = separatorIndex === -1 ? 0 : small.number.length - separatorIndex - 1;
    const sampleValue = Number(separatorIndex === -1 ? small.number : `${small.number.slice(0, separatorIndex)}.${small.number.slice(separatorIndex + 1)}`);

    let divisor = sampleValue > 0 ? SAMPLE_AMOUNT / sampleValue : 0;
    let thousandsSeparator = '';

    const large = splitSample(toWestern(config.sampleLarge));
    if (large) {
      const integerPart = decimals > 0 ? large.number.slice(0, large.number.length - decimals - 1) : large.number;
      const grouping = integerPart.match(/\D/);
      thousandsSeparator = grouping ? grouping[0] : '';
      if (!divisor) {
        const largeValue = Number(integerPart.replace(/\D/g, ''));
        divisor = largeValue > 0 ? Math.round(SAMPLE_LARGE_AMOUNT / largeValue) : 0;
      }
    }

    if (!divisor) return fallback;
    if (arabic) return { prefix: small.prefix, suffix: small.suffix, divisor, decimals, decimalSeparator: ARABIC_DECIMAL, thousandsSeparator: thousandsSeparator ? ARABIC_THOUSANDS : '', arabic };
    return { prefix: small.prefix, suffix: small.suffix, divisor, decimals, decimalSeparator, thousandsSeparator, arabic };
  }

  /**
   * Formats an amount exactly as the Liquid `money` snippet would.
   * @param {number|string} amount - A price as Shopify's APIs return it (e.g. 1990 for 19.900 KD).
   * @returns {string}
   */
  function money(amount) {
    const format = Kanze.moneyFormat;
    const value = Number(amount);
    if (!Number.isFinite(value)) return '';

    const fixed = (Math.abs(value) / format.divisor).toFixed(format.decimals);
    const [integer, fraction] = fixed.split('.');
    const grouped = format.thousandsSeparator ? integer.replace(/\B(?=(\d{3})+(?!\d))/g, format.thousandsSeparator) : integer;
    const western = fraction ? `${grouped}${format.decimalSeparator}${fraction}` : grouped;
    const number = format.arabic ? toArabic(western) : western;

    return `${value < 0 ? '-' : ''}${format.prefix}${number}${format.suffix}`;
  }

  let config = {};
  try {
    config = JSON.parse(document.getElementById('KanzeConfig').textContent);
  } catch (error) {
    console.error('Kanze: could not read the theme configuration.', error);
  }

  const Kanze = (window.Kanze = window.Kanze || {});
  Kanze.moneyFormat = readFormat(config.money);
  Kanze.money = money;
  /**
   * A price as a plain number in the store's currency (1990 → 19.9), for counting, never for
   * showing: what is shown goes through Kanze.money. The divisor is the one read from the samples.
   */
  Kanze.amount = (amount) => {
    const value = Number(amount);
    return Number.isFinite(value) ? Number((value / Kanze.moneyFormat.divisor).toFixed(Kanze.moneyFormat.decimals)) : undefined;
  };
  /**
   * Writes a number the way the page's language wants it: Arabic-Indic digits on Arabic
   * pages when the theme setting is on, unchanged everywhere else.
   * @param {number|string} value
   * @returns {string}
   */
  Kanze.digits = (value) => (config.arabicDigits ? toArabic(value) : String(value));

  /**
   * <kanze-money amount="1990"></kanze-money>
   * Shows a price and keeps it up to date when the `amount` attribute changes.
   * Server-rendered text inside the element is left alone until then.
   */
  class KanzeMoney extends HTMLElement {
    static observedAttributes = ['amount'];

    attributeChangedCallback(name, previous, next) {
      if (next === null || next === '') return;
      if (previous === null && this.textContent.trim() !== '') return;
      this.textContent = Kanze.money(next);
    }
  }

  if (!customElements.get('kanze-money')) customElements.define('kanze-money', KanzeMoney);

  /* ---------- Keyboard or finger ----------
   * A ring around what has focus is for someone using the keyboard. The theme moves focus by
   * itself (into a panel that opened, back to the button that opened it), and after a touch
   * that drew a black box around a link nobody had chosen. So <html> carries `no-keys` until
   * Tab or an arrow key is pressed, and again after the next touch or click; the styles draw
   * no ring while it is there (assets/critical.css). Keyboard users lose nothing: the ring is
   * on from their first Tab.
   */
  Kanze.keys = false;
  document.documentElement.classList.add('no-keys');
  document.addEventListener(
    'keydown',
    (event) => {
      if (event.key !== 'Tab' && !String(event.key).startsWith('Arrow')) return;
      Kanze.keys = true;
      document.documentElement.classList.remove('no-keys');
    },
    true
  );
  document.addEventListener(
    'pointerdown',
    () => {
      Kanze.keys = false;
      document.documentElement.classList.add('no-keys');
    },
    true
  );

  /**
   * Moves focus from code, without scrolling to it. Browsers that know how are also told not
   * to mark it as focused when the keyboard is not in use.
   * @param {HTMLElement|null|undefined} element
   */
  Kanze.focus = (element) => element?.focus({ preventScroll: true, focusVisible: Kanze.keys });

  /* ---------- Dialogs (drawers, bottom sheets, pop-ups) ----------
   * <button data-open-dialog="MenuDrawer"> opens <dialog id="MenuDrawer">.
   * [data-close-dialog] inside a dialog closes it. A dialog with
   * `data-backdrop-close` also closes when the dimmed area around it is clicked.
   * A link with data-open-dialog still works as a normal link if its dialog is missing.
   */

  /**
   * Opens a <dialog> as a modal and returns focus to the opener when it closes.
   * @param {HTMLDialogElement} dialog
   * @param {HTMLElement} [opener]
   */
  Kanze.openDialog = (dialog, opener) => {
    if (!dialog || dialog.open) return;
    dialog.showModal();
    // Opened by a touch or a click: focus goes to the panel itself, not to the first link in
    // it, so nothing in it looks chosen. With the keyboard it goes to the first control, as
    // the browser does. (A panel that wants its own field focused does that on `dialog:open`.)
    if (!Kanze.keys) {
      if (!dialog.hasAttribute('tabindex')) dialog.setAttribute('tabindex', '-1');
      Kanze.focus(dialog);
    }
    opener?.setAttribute('aria-expanded', 'true');
    dialog.dispatchEvent(new CustomEvent('dialog:open', { bubbles: true, detail: { opener } }));
    dialog.addEventListener(
      'close',
      () => {
        opener?.setAttribute('aria-expanded', 'false');
        if (opener?.isConnected) Kanze.focus(opener);
      },
      { once: true }
    );
  };

  document.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    const opener = target.closest('[data-open-dialog]');
    if (opener) {
      const dialog = document.getElementById(opener.dataset.openDialog);
      if (dialog instanceof HTMLDialogElement) {
        event.preventDefault();
        Kanze.openDialog(dialog, opener);
      }
      return;
    }

    if (target.closest('[data-close-dialog]')) {
      target.closest('dialog')?.close();
      return;
    }

    if (target instanceof HTMLDialogElement && target.hasAttribute('data-backdrop-close')) target.close();
  });

  /* A <select data-submit-on-change> sends its form as soon as a choice is made. */
  document.addEventListener('change', (event) => {
    const field = event.target instanceof Element ? event.target.closest('[data-submit-on-change]') : null;
    field?.form?.requestSubmit();
  });

  /* <details data-collapse-mobile> starts closed on phones and is always open on larger screens. */
  const desktop = window.matchMedia('(min-width: 750px)');
  const syncCollapsibles = () => {
    document.querySelectorAll('details[data-collapse-mobile]').forEach((details) => {
      details.open = desktop.matches;
    });
  };
  desktop.addEventListener('change', syncCollapsibles);
  syncCollapsibles();
  document.addEventListener('shopify:section:load', syncCollapsibles);

  /* ---------- Bag (cart) ----------
   * One place for every change to the bag. Each call also asks Shopify to
   * re-render the bag sections on the page (any element with
   * [data-cart-section="<section id>"]), swaps their [data-cart-contents="…"] regions, and
   * fires `cart:updated` on document. After an add the event also says what was added and
   * where on the page it came from (`detail.added`), which is what the flight to the bag and
   * the "added" confirmation go by (assets/bag-added.js). Adding never opens the bag.
   */
  const routes = config.routes || {};
  const strings = config.strings || {};
  Kanze.routes = routes;

  Kanze.cart = {
    sectionIds() {
      return [...new Set([...document.querySelectorAll('[data-cart-section]')].map((element) => element.dataset.cartSection))];
    },

    async request(route, body) {
      let response;
      try {
        response = await fetch(`${route}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ ...body, sections: this.sectionIds().join(','), sections_url: window.location.pathname }),
        });
      } catch (error) {
        // No answer at all: the connection dropped.
        throw Object.assign(new Error(strings.cartError || ''), { code: 'network' });
      }
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw Object.assign(new Error(data.description || data.message || strings.cartError || ''), { code: 'refused', status: response.status });
      return data;
    },

    async fetch() {
      const response = await fetch(`${routes.cart}.js`, { headers: { Accept: 'application/json' } });
      return response.json();
    },

    /**
     * Why Shopify refused an add, in plain words. It does not say which, in a way a script can
     * read in every language, so the bag is asked: the size is already in it (that was the
     * last of it), or it is not (it just sold out).
     */
    async explain(error, items) {
      if (error.code !== 'refused' || error.status !== 422) return error;
      const cart = await this.fetch().catch(() => null);
      if (!cart) return error;
      const wanted = items.map((item) => Number(item.id));
      const inBag = cart.items.some((line) => wanted.includes(line.variant_id));
      return Object.assign(new Error((inBag ? strings.cartLimit : strings.cartSoldOut) || error.message), { code: inBag ? 'limit' : 'sold-out' });
    },

    /**
     * The bag as Shopify has just drawn it. Every bag section carries its own state as data
     * (snippets/bag-state.liquid), so the answer to "add this" already says what the bag
     * holds: no second request is made for it.
     * @param {Object<string, string>|undefined} sections - Section id to HTML, from Shopify's answer
     * @returns {{item_count: number, total_price: number, items: object[]}|null}
     */
    read(sections) {
      for (const html of Object.values(sections || {})) {
        if (!html || !html.includes('data-bag-state')) continue;
        try {
          const state = JSON.parse(new DOMParser().parseFromString(html, 'text/html').querySelector('script[data-bag-state]').textContent);
          return { item_count: state.count, total_price: state.total, items: state.items.map((item) => ({ key: item.key, handle: item.h, id: item.id, variant_id: item.id, final_price: item.p, quantity: item.q, product_title: item.t })) };
        } catch (error) {
          // Not readable: the bag is asked for the old way.
        }
      }
      return null;
    },

    /**
     * One request: Shopify adds the pieces and draws the bag's sections again in the same
     * answer. The page is told twice. At once, before anything is sent (`cart:adding`), so
     * that the photo can fly and the confirmation can show within a frame of the tap,
     * whatever the network. And when Shopify has answered: `cart:updated` when it went
     * through, `cart:add-failed` when it did not, so that what was shown is taken back.
     * @param {{id: number, quantity: number, properties?: object}[]} items
     * @param {{from?: (Element|null)[], origin?: Element, quiet?: boolean, ready?: Function, shown?: object[]}} [how] - `from`: the
     *   photo of each piece on the page, in the same order, for the flight to the bag. `origin`: the
     *   button that was pressed, where the flight starts when the photo is not on screen. `quiet`: no
     *   flight and no confirmation (putting a removed piece back). `ready`: run once the add has gone
     *   through, before the page is told (the size sheet closes here). `shown`: what the page already
     *   says about each piece ({ title, options, price }), for the confirmation while it waits.
     *   `track`: for the tracking plan's `Add to Cart`, where it was added from and how each
     *   size came to be chosen ({ source, sizes: { [variant id]: 'manual' | … } }).
     */
    async add(items, { from = [], origin = null, quiet = false, ready = null, shown = [], track = null } = {}) {
      const turn = (this.turn = (this.turn || 0) + 1);
      if (!quiet) document.dispatchEvent(new CustomEvent('cart:adding', { detail: { turn, items, from, origin, shown } }));
      let data;
      let cart;
      try {
        data = await this.request(routes.cartAdd, { items });
        // An answer that says nothing about the add (a page in place of data, as a robot check
        // sends) is no answer: what was shown at the tap must not stay as if it had gone through.
        if (!data.sections && !data.items && !data.id) throw Object.assign(new Error(strings.cartError || ''), { code: 'network' });
        cart = this.read(data.sections) || (await this.fetch().catch(() => null));
        if (!cart) throw Object.assign(new Error(strings.cartError || ''), { code: 'network' });
      } catch (error) {
        if (!quiet) document.dispatchEvent(new CustomEvent('cart:add-failed', { detail: { turn } }));
        const why = await this.explain(error, items);
        if (!quiet) {
          const reason = { 'sold-out': 'sold_out', limit: 'limit', network: 'network' }[why.code] || 'other';
          window.kz?.track?.('Add to Cart Failed', { reason, error: [error.code, error.status].filter(Boolean).join(' ') || 'unknown', source: track?.source });
        }
        throw why;
      }
      ready?.();
      this.publish(cart, data.sections, quiet ? null : { turn, items: data.items || [data], from, origin });
      if (!quiet) this.count(data.items || [data], track, items);
      return cart;
    },

    /**
     * `Add to Cart`, once for each piece Shopify has just added, in the tracking plan's words.
     * @param {object[]} lines - The lines of Shopify's answer  @param {{source?: string, sizes?: object}|null} track
     * @param {{id: number, quantity: number}[]} asked - What was asked for: a line's own quantity is all of it in the bag
     */
    count(lines, track, asked = []) {
      const names = config.optionNames || {};
      const option = (line, kind) => (line.options_with_values || []).find((entry) => (names[kind] || []).includes(String(entry.name).trim().toLowerCase()))?.value;
      for (const line of lines) {
        const id = line.variant_id || line.id;
        window.kz?.track?.('Add to Cart', {
          product_handle: line.handle,
          product_title: line.product_title,
          variant_id: id,
          size: option(line, 'size'),
          color: option(line, 'color'),
          price: Kanze.amount(line.final_price ?? line.price),
          quantity: asked.find((item) => Number(item.id) === id)?.quantity ?? line.quantity,
          source: track?.source,
          size_source: track?.sizes?.[id],
        });
      }
    },

    /** @param {string} key - The line item key  @param {number} quantity - 0 removes the line */
    async change(key, quantity) {
      const cart = await this.request(routes.cartChange, { id: key, quantity });
      this.publish(cart, cart.sections);
      return cart;
    },

    /** @param {object} payload - e.g. { note: 'Leave at the door' } */
    async update(payload) {
      const cart = await this.request(routes.cartUpdate, payload);
      this.publish(cart, cart.sections);
      return cart;
    },

    /**
     * Another size of a piece already in the bag: the new size goes in first, and only then
     * does the old one leave. If the new size cannot be added, nothing has changed.
     * @param {string} key - The line to replace  @param {number} id - The new variant  @param {number} quantity
     */
    async swap(key, id, quantity) {
      const items = [{ id, quantity }];
      try {
        await this.request(routes.cartAdd, { items });
      } catch (error) {
        throw await this.explain(error, items);
      }
      return this.change(key, 0);
    },

    /** Asks Shopify for the bag as it is now, for a page that was shown again from the browser's memory. */
    async refresh() {
      const ids = this.sectionIds();
      const [cart, sections] = await Promise.all([this.fetch(), ids.length ? fetch(`${window.location.pathname}?sections=${ids.join(',')}`).then((response) => (response.ok ? response.json() : null)) : null]);
      this.publish(cart, sections);
      return cart;
    },

    publish(cart, sections, added = null) {
      if (sections) {
        document.querySelectorAll('[data-cart-section]').forEach((element) => {
          const html = sections[element.dataset.cartSection];
          if (!html) return;
          const fresh = new DOMParser().parseFromString(html, 'text/html');
          element.querySelectorAll('[data-cart-contents]').forEach((region) => {
            const replacement = fresh.querySelector(`[data-cart-contents="${region.dataset.cartContents}"]`);
            if (replacement) region.innerHTML = replacement.innerHTML;
          });
        });
      }
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart, added } }));
    },

    /** Opens the bag because he asked for it: the drawer if there is one, otherwise the bag page. */
    open(opener) {
      const drawer = document.getElementById('CartDrawer');
      if (drawer) Kanze.openDialog(drawer, opener || document.querySelector('.header__bag'));
      else window.location.assign(routes.cart);
    },
  };

  // A page shown again by the Back button can hold the bag as it was when he left it.
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) Kanze.cart.refresh().catch(() => {});
  });

  /**
   * <app-promo>
   * The "get the app" bar. Who sees it, and whether it is still closed, is decided by the
   * short script in snippets/app-promo.liquid before the page is painted. This part only
   * remembers the moment the close button was pressed, so the bar stays away for the
   * number of days set in Theme settings > App promo.
   */
  class AppPromo extends HTMLElement {
    static storageKey = 'kanze:app-promo-closed';

    connectedCallback() {
      this.querySelector('[data-dismiss]')?.addEventListener('click', () => {
        this.hidden = true;
        try {
          localStorage.setItem(AppPromo.storageKey, String(Date.now()));
        } catch (error) {
          /* Storage can be blocked. The bar simply comes back on the next page. */
        }
      });
    }
  }

  if (!customElements.get('app-promo')) customElements.define('app-promo', AppPromo);

  /**
   * <footer-wordmark data-reveal>
   * The KANZE name across the footer is uncovered from the bottom up as it scrolls into view,
   * once (the look is in assets/section-footer.css). It is covered only while it is still
   * below the screen, so nobody ever sees it disappear: on a page so short that the footer is
   * already on screen, it simply stands there. Nothing happens for a visitor who asked for
   * less motion.
   */
  class FooterWordmark extends HTMLElement {
    connectedCallback() {
      if (!this.hasAttribute('data-reveal') || !('IntersectionObserver' in window)) return;
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      if (this.getBoundingClientRect().top < window.innerHeight) return;
      this.classList.add('will-reveal');
      this.observer = new IntersectionObserver(
        (entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          this.classList.add('is-in');
          this.observer.disconnect();
        },
        { threshold: 0.35 }
      );
      this.observer.observe(this);
    }

    disconnectedCallback() {
      this.observer?.disconnect();
    }
  }

  if (!customElements.get('footer-wordmark')) customElements.define('footer-wordmark', FooterWordmark);
})();
